<template>
  <div class="landing-container">
    <!-- Enhanced Subscription Section -->
    <div class="subscription-section">
      <div class="subscription-card" :class="{ 'subscribed': isSubscribed }">
        <transition name="fade" mode="out-in">
          <div v-if="!isSubscribed" key="form">
            <h2>Stay Sharp Weekly ✨</h2>
            <p>Get fresh insights, tools, and ideas every week straight to your inbox. No fluff, just value.</p>
            
            <form @submit.prevent="subscribe" class="subscription-form">
              <div class="input-group">
                <input
                  type="email"
                  v-model="subscriptionEmail"
                  required
                  placeholder="Enter Your Email"
                  class="subscription-input"
                />
              </div>
              <button type="submit" class="subscription-button">
                <span v-if="!isLoading">Subscribe</span>
                <span v-else class="spinner"></span>
              </button>
            </form>
          </div>
          
          <div v-else key="success" class="success-message">
            <i class="fas fa-check-circle"></i>
            <h3>You're subscribed!</h3>
            <p>Thanks for joining. Watch your inbox for our first email.</p>
          </div>
        </transition>
      </div>
    </div>

    <!-- Main Content -->
    <div class="hero-section">
      <h1 class="title">Jerome Brown</h1>
      <p class="subtitle">Technology Enthusiast & Innovator</p>
      <a href="mailto:jbreview@outlook.com" class="email-link">jbreview@outlook.com</a>
      <button class="scroll-btn">
        <span class="arrow-down">↓</span>
      </button>
    </div>

    <!-- Company and Product Links -->
    <div class="company-links">
      <a href="https://sisoftware.xyz" target="_blank" class="company-link" aria-label="SI Software">
        Visit SI Software
      </a>
      <a href="https://thryveconnect.com" target="_blank" class="product-link" aria-label="Thryve Connect">
        Explore Thryve!
      </a>
      <a href="https://adametrics.vercel.app/" target="_blank" class="product-link" aria-label="Adametrics">
        Try Adametrics in Beta!
      </a>
      <a href="https://savvyfi.vercel.app/" target="_blank" class="product-link" aria-label="SavvyFi">
        Freelancers can be Finance Pros too
      </a>
    </div>

    <!-- Navigation Sidebar -->
    <nav class="nav-bar" :class="{ 'scrolled': isScrolled }">
      <h3 class="nav-title">Join My Journey</h3>
      <ul class="nav-list">
        <li
          class="nav-item"
          v-for="(link, index) in links"
          :key="index"
          :style="getAnimationStyle(index)"
        >
          <router-link
            v-if="link.path"
            :to="link.path"
            class="nav-link"
          >
            {{ link.name }}
          </router-link>
          <a
            v-else
            :href="link.href"
            target="_blank"
            class="nav-link"
          >
            {{ link.name }}
          </a>
        </li>
      </ul>
    </nav>

    <!-- Technology Icons and Built With Label -->
    <div class="tech-footer">
      <p class="built-with">Built with</p>
      <div class="tech-icons">
        <a href="https://vuejs.org/" target="_blank" class="icon-link" aria-label="Vue.js">
          <img src="https://upload.wikimedia.org/wikipedia/commons/9/95/Vue.js_Logo_2.svg" alt="Vue.js logo" />
        </a>
        <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript" target="_blank" class="icon-link" aria-label="JavaScript">
          <img src="https://upload.wikimedia.org/wikipedia/commons/6/6a/JavaScript-logo.png" alt="JavaScript logo" />
        </a>
        <a href="https://developer.mozilla.org/en-US/docs/Web/CSS" target="_blank" class="icon-link" aria-label="CSS3">
          <img src="https://upload.wikimedia.org/wikipedia/commons/6/62/CSS3_logo.svg" alt="CSS3 logo" />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import emailjs from "emailjs-com";

export default {
  data() {
    return {
      isScrolled: false,
      links: [
        { name: 'Home', path: '/' },
        { name: 'About Me', path: '/about' },
        { name: 'Contact', path: '/contact' },
        { name: 'I write here', href: 'https://jeromejbrown.substack.com/' },
      ],
      subscriptionEmail: '',
      isSubscribed: false,
      isLoading: false,
      subscriptionStatus: ''
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    getAnimationStyle(index) {
      return {
        animation: `fadeSlideIn 0.5s ease-out ${index * 0.1}s`,
        'animation-fill-mode': 'both',
      };
    },
    async subscribe() {
      this.isLoading = true;
      this.subscriptionStatus = '';
      
      try {
        // Send to your email via EmailJS
        await emailjs.send(
          "service_dglkfy8",
          "template_gbwxpgd",
          {
            from_email: this.subscriptionEmail,
            reason: "Newsletter Subscription",
            message: "New newsletter subscriber: " + this.subscriptionEmail
          },
          "Ma-s_nnSD1NmNYYXu"
        );
        
        this.isSubscribed = true;
        this.subscriptionEmail = '';
      } catch (error) {
        console.error('Subscription failed:', error);
        this.subscriptionStatus = 'Subscription failed. Please try again.';
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Google Font Import */
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;700&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css');

/* Animation Keyframes */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes bounceIn {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes fadeSlideIn {
  0% {
    opacity: 0;
    transform: translateY(15px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Landing Container */
.landing-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, #1A2A44 0%, #2A3B5A 100%);
  color: #FFFFFF;
  padding: 20px;
  font-family: 'Poppins', sans-serif;
  position: relative;
  overflow: hidden;
  animation: fadeIn 1.5s ease-out;
}

/* Enhanced Subscription Section */
.subscription-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  margin: 2rem auto;
  max-width: 800px;
  width: 90%;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.subscription-card {
  background: linear-gradient(145deg, #2A3B5A, #1A2A44);
  padding: 2.5rem;
  border-radius: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  text-align: center;
  transition: all 0.4s ease;
}

.subscription-card h2 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 1rem;
}

.subscription-card p {
  color: #E5E7EB;
  margin: 1rem 0 2rem;
  font-size: 1.1rem;
  line-height: 1.6;
}

.subscription-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.input-group {
  position: relative;
}

.input-group i {
  position: absolute;
  left: 1.2rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}

.subscription-input {
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  color: white;
  transition: all 0.3s ease;
}

.subscription-input:focus {
  outline: none;
  border-color: #3B82F6;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

.subscription-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.subscription-button {
  padding: 1rem;
  background: linear-gradient(135deg, #3B82F6, #2563EB);
  color: white;
  font-weight: 600;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
}

.subscription-button:hover {
  background: linear-gradient(135deg, #2563EB, #1D4ED8);
  transform: translateY(-2px);
}

.success-message {
  padding: 1rem;
}

.success-message i {
  font-size: 3rem;
  color: #10B981;
  margin-bottom: 1rem;
}

.success-message h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: white;
}

.success-message p {
  color: #E5E7EB;
  margin-bottom: 0;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Hero Section */
.hero-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 80vh;
}

.title {
  font-size: 5rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 20px;
  animation: slideInLeft 1.5s ease-out;
}

.subtitle {
  font-size: 1.5rem;
  color: #E5E7EB;
  margin-bottom: 30px;
}

.email-link {
  color: #2DD4BF;
  font-size: 1.2rem;
  text-decoration: none;
  margin-bottom: 40px;
}

.email-link:hover {
  text-decoration: underline;
}

.scroll-btn {
  background: #FFFFFF;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  animation: bounceIn 1.5s ease-out;
}

.arrow-down {
  font-size: 1.5rem;
  color: #1A2A44;
}

/* Company Links */
.company-links {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 40px 0;
}

.company-link,
.product-link {
  font-size: 1.2rem;
  font-weight: 500;
  color: #2DD4BF;
  text-decoration: none;
  transition: transform 0.3s, color 0.3s;
}

.company-link:hover,
.product-link:hover {
  transform: scale(1.05);
  color: #26B8A6;
}

/* Navigation Sidebar */
.nav-bar {
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 20px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: background 0.3s ease;
  z-index: 10;
}

.nav-bar.scrolled {
  background: rgba(255, 255, 255, 0.2);
}

.nav-title {
  font-size: 1.2rem;
  color: #FFFFFF;
  margin-bottom: 10px;
  text-transform: uppercase;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
}

.nav-list {
  list-style-type: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
}

.nav-item {
  position: relative;
}

.nav-link {
  color: #E5E7EB;
  font-size: 1rem;
  font-weight: 400;
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
  transition: color 0.3s ease, transform 0.3s ease;
}

.nav-link:hover {
  color: #2DD4BF;
  transform: translateX(5px);
}

/* Technology Icons Section */
.tech-footer {
  position: fixed;
  bottom: 20px;
  left: 20px;
  text-align: left;
}

.built-with {
  font-size: 1rem;
  color: #E5E7EB;
  margin-bottom: 5px;
}

.tech-icons {
  display: flex;
  gap: 15px;
}

.icon-link img {
  width: 30px;
  height: 30px;
  transition: transform 0.3s ease;
}

.icon-link img:hover {
  animation: pulse 1s infinite;
}
</style>